import React, { FC, Fragment, ReactNode } from 'react';

import { FormattedList, FormattedMessage } from 'react-intl';

import CommonMessages from '~/core/commonMessages';
import { formatBroadcasters, parseValueFromLocalizedAttribute } from '~/core/utils';

import { GameInfo as GameInfoType, CommonGCGame, Scratch } from '../../../../types';

import {
    GameInfoContainer,
    GameInfoDetails,
    InfoCategory,
    InfoContainer,
    OfficialContainer,
    OfficialsContainer,
} from './styles';

type Props = {
    game: CommonGCGame;
    gameInfo: GameInfoType;
};

const coachCategory = (team: string): ReactNode => (
    <FormattedMessage
        description="A phrase for the head coach of a team"
        defaultMessage="{teamAbbrev} Coach"
        values={{
            teamAbbrev: team,
        }}
    />
);

const formatList = (list: Array<ReactNode> | undefined): ReactNode =>
    list?.length ? (
        <FormattedList value={list} type="unit" />
    ) : (
        <FormattedMessage {...CommonMessages.statsDescriptions.symbols.noInfo} />
    );

const formatScratchesNames = (scratches: Array<Scratch>): Array<string> =>
    scratches?.map(
        (scratch) =>
            parseValueFromLocalizedAttribute(scratch.firstName) +
            ' ' +
            parseValueFromLocalizedAttribute(scratch.lastName)
    );

const formatOfficials = (isReferee: boolean, officials: Array<string>): Array<ReactNode> =>
    officials.map((official) => (
        <OfficialContainer key={official}>
            <FormattedMessage
                description="An official's name and their role abbreviation"
                defaultMessage="{official} - {abbrev}"
                values={{
                    official,
                    abbrev: (
                        <FormattedMessage
                            {...CommonMessages.gameOfficialsDescriptions.abbreviations[
                                isReferee ? 'referee' : 'linesperson'
                            ]}
                        />
                    ),
                }}
            />
        </OfficialContainer>
    ));

const formatScratchTitle = (teamAbbrev: string): ReactNode => (
    <InfoCategory role="heading" aria-level={3}>
        {teamAbbrev}&nbsp;
        <FormattedMessage {...CommonMessages.playerInfoDescriptions.long.scratches} />
    </InfoCategory>
);

const GameInfo: FC<Props> = ({ game, gameInfo }) => {
    const information: Array<ReactNode> = [];

    if (game.tvBroadcasts && game.tvBroadcasts.length > 0) {
        information.push(
            <InfoContainer key="tv-broadcasts">
                <InfoCategory role="heading" aria-level={3}>
                    <FormattedMessage {...CommonMessages.gameInformationDescriptions.networks} />
                </InfoCategory>
                <GameInfoDetails>{formatBroadcasters(game.tvBroadcasts)}</GameInfoDetails>
            </InfoContainer>
        );
    }

    if (game.venue) {
        information.push(
            <InfoContainer key="venue">
                <InfoCategory role="heading" aria-level={3}>
                    <FormattedMessage {...CommonMessages.gameLocationDescriptions.long.location} />
                </InfoCategory>
                <GameInfoDetails>
                    {parseValueFromLocalizedAttribute(game.venue)}
                    {game.venueLocation && (
                        <>
                            <FormattedMessage {...CommonMessages.symbols.comma} />
                            &nbsp;
                            {parseValueFromLocalizedAttribute(game.venueLocation)}
                        </>
                    )}
                </GameInfoDetails>
            </InfoContainer>
        );
    }

    const referees =
        gameInfo.referees.length > 0
            ? gameInfo.referees.map((official) => parseValueFromLocalizedAttribute(official))
            : undefined;
    const linespersons =
        gameInfo.linesmen.length > 0
            ? gameInfo.linesmen.map((official) => parseValueFromLocalizedAttribute(official))
            : undefined;

    if (referees !== undefined || linespersons !== undefined) {
        information.push(
            <InfoContainer key="Officials">
                <InfoCategory role="heading" aria-level={3}>
                    <FormattedMessage {...CommonMessages.gameOfficialsDescriptions.long.officials} />
                </InfoCategory>
                <OfficialsContainer>
                    {referees && formatOfficials(true, referees)}
                    {linespersons && formatOfficials(false, linespersons)}
                </OfficialsContainer>
            </InfoContainer>
        );
    }

    if (gameInfo.awayTeam.headCoach) {
        information.push(
            <InfoContainer key="away-coach">
                <InfoCategory role="heading" aria-level={3}>
                    {coachCategory(game.awayTeam.abbrev)}
                </InfoCategory>
                <GameInfoDetails>{parseValueFromLocalizedAttribute(gameInfo.awayTeam.headCoach)}</GameInfoDetails>
            </InfoContainer>
        );
    }

    if (gameInfo.homeTeam.headCoach) {
        information.push(
            <InfoContainer key="home-coach">
                <InfoCategory role="heading" aria-level={3}>
                    {coachCategory(game.homeTeam.abbrev)}
                </InfoCategory>
                <GameInfoDetails>{parseValueFromLocalizedAttribute(gameInfo.homeTeam.headCoach)}</GameInfoDetails>
            </InfoContainer>
        );
    }

    if (gameInfo.awayTeam.scratches.length > 0 || gameInfo.homeTeam.scratches.length > 0) {
        information.push(
            <Fragment key="scratches">
                {gameInfo.awayTeam.scratches.length > 0 && (
                    <InfoContainer key="away-scratches">
                        {formatScratchTitle(game.awayTeam.abbrev)}
                        <GameInfoDetails>
                            {formatList(formatScratchesNames(gameInfo?.awayTeam.scratches))}
                        </GameInfoDetails>
                    </InfoContainer>
                )}
                {gameInfo.homeTeam.scratches.length > 0 && (
                    <InfoContainer key="home-scratches">
                        {formatScratchTitle(game.homeTeam.abbrev)}
                        <GameInfoDetails>
                            {formatList(formatScratchesNames(gameInfo?.homeTeam.scratches))}
                        </GameInfoDetails>
                    </InfoContainer>
                )}
            </Fragment>
        );
    }

    return <GameInfoContainer>{information}</GameInfoContainer>;
};

export default GameInfo;
